"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelectableButtonGroupWidget = exports.SelectableButtonGroupSelectionBehavior = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var Declarative_1 = require("../utils/Declarative");
var FindIndex_1 = require("../utils/FindIndex");
var ReactHelpers_1 = require("../utils/ReactHelpers");
var SelectableButtonGroupSelectionBehavior;
(function (SelectableButtonGroupSelectionBehavior) {
    /** Multiple buttons can be selected within the group. */
    SelectableButtonGroupSelectionBehavior["Multiple"] = "Multiple";
    /** A single button (at most) can be selected within the group. */
    SelectableButtonGroupSelectionBehavior["Single"] = "Single";
})(SelectableButtonGroupSelectionBehavior = exports.SelectableButtonGroupSelectionBehavior || (exports.SelectableButtonGroupSelectionBehavior = {}));
// eslint-disable-next-line max-len
var SelectableButtonGroupWidget = /** @class */ (function (_super) {
    tslib_1.__extends(SelectableButtonGroupWidget, _super);
    function SelectableButtonGroupWidget(props) {
        var _this = _super.call(this, props) || this;
        _this.childButtons = [];
        _this.buttonElements = [];
        _this.handleClick = function (e) {
            var buttonElement = e.currentTarget;
            var buttonIndex = FindIndex_1.findIndex(_this.childButtons, function (button, index) { return buttonElement === _this.buttonElements[index]; });
            var selected = _this.childButtons[buttonIndex].props.selected;
            if (_this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single && selected) {
                return;
            }
            if (_this.props.onChange) {
                _this.props.onChange(buttonIndex, !selected);
            }
        };
        _this.handleFocus = ReactHelpers_1.mergeEventHandlers(function (e) {
            var target = e.target;
            var focusIndex = FindIndex_1.findIndex(_this.childButtons, function (button, index) { return target === _this.buttonElements[index] && !button.props.disabled; });
            if (focusIndex >= 0 && focusIndex !== _this.state.focusIndex) {
                _this.setState({ focusIndex: focusIndex });
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            var buttonCount = _this.childButtons.length;
            var increment = 0;
            var focusIndex = _this.state.focusIndex;
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                increment = -1;
            }
            else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                increment = 1;
            }
            else if (e.key === 'Home') {
                focusIndex = -1;
                increment = 1;
            }
            else if (e.key === 'End') {
                focusIndex = buttonCount;
                increment = -1;
            }
            else {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            do {
                focusIndex = (focusIndex + increment + buttonCount) % buttonCount;
                if (focusIndex === _this.state.focusIndex) {
                    return;
                }
            } while (_this.childButtons[focusIndex].props.disabled);
            var isSingleSelect = _this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single;
            if (isSingleSelect && !_this.isButtonSelected(focusIndex) && _this.props.onChange) {
                _this.props.onChange(focusIndex, true);
            }
            if (focusIndex !== _this.state.focusIndex) {
                _this.setState({ focusIndex: focusIndex }, function () { var _a; return (_a = _this.buttonElements[_this.state.focusIndex]) === null || _a === void 0 ? void 0 : _a.focus(); });
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.updateChildButtons();
        _this.state = { focusIndex: _this.getInitialFocusIndex() };
        return _this;
    }
    SelectableButtonGroupWidget.prototype.componentDidUpdate = function (prevProps, prevState) {
        var newFocusIndex = this.getInitialFocusIndex();
        if (prevState.focusIndex < 0 && newFocusIndex >= 0) {
            this.setState({ focusIndex: newFocusIndex });
        }
    };
    SelectableButtonGroupWidget.prototype.updateChildButtons = function () {
        var childButtons = [];
        var selectedButtonsCount = 0;
        React.Children.map(this.props.children, function (child) {
            if (child.props.buttonType !== undefined) {
                childButtons.push(child);
                if (child.props.selected) {
                    selectedButtonsCount++;
                }
            }
        });
        if (childButtons.length < 2) {
            throw Error("Invalid button count: 2+ are required");
        }
        if (this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single && selectedButtonsCount > 1) {
            // eslint-disable-next-line max-len
            throw Error("Invalid selected button count: SelectableButtonGroupSelectionBehavior.Single requires one or zero buttons to be selected. " + selectedButtonsCount + " are selected");
        }
        this.childButtons = childButtons;
    };
    SelectableButtonGroupWidget.prototype.getInitialFocusIndex = function () {
        var focusIndex = FindIndex_1.findIndex(this.childButtons, function (button) { return !!button.props.selected && !button.props.disabled; });
        if (focusIndex < 0) {
            focusIndex = FindIndex_1.findIndex(this.childButtons, function (button) { return !button.props.disabled; });
        }
        return focusIndex;
    };
    SelectableButtonGroupWidget.prototype.isButtonSelected = function (index) {
        return !!this.childButtons[index].props.selected;
    };
    SelectableButtonGroupWidget.prototype.renderChildren = function () {
        var _this = this;
        return React.Children.map(this.props.children, function (child, buttonIndex) {
            if (child.props.buttonType === undefined) {
                return child;
            }
            var buttonPropsOverride = {
                buttonRef: function (buttonElem) { return buttonElem && (_this.buttonElements[buttonIndex] = buttonElem); },
                disableTabAccessibility: _this.state.focusIndex !== buttonIndex,
                handleClick: _this.handleClick,
                key: buttonIndex,
                role: _this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single ? 'radio' : 'button',
                selected: !!child.props.selected
            };
            return React.cloneElement(child, buttonPropsOverride);
        });
    };
    SelectableButtonGroupWidget.prototype.render = function () {
        var _a = this.props, children = _a.children, onChange = _a.onChange, selectionBehavior = _a.selectionBehavior, testId = _a.testId, htmlAttrs = tslib_1.__rest(_a, ["children", "onChange", "selectionBehavior", "testId"]);
        this.updateChildButtons();
        var isSingleSelect = this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single;
        return (React.createElement("div", tslib_1.__assign({ "aria-label": this.props.title, "aria-multiselectable": !isSingleSelect || undefined, "data-tb-test-id": Declarative_1.makeTestId(this.props.testId, 'SelectableButtonGroup'), role: isSingleSelect ? 'radiogroup' : undefined }, htmlAttrs, { onFocus: this.handleFocus, onKeyDown: this.handleKeyDown }), this.renderChildren()));
    };
    return SelectableButtonGroupWidget;
}(React.Component));
exports.SelectableButtonGroupWidget = SelectableButtonGroupWidget;
//# sourceMappingURL=SelectableButtonGroupWidget.js.map